import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { getUIErrorMessage } from '@/utils/errors';
import { Button } from '@/components/ui/button';
import { updateProcessObjectFile } from './constants';
import { toast } from 'sonner';
import ErrorAlert from '@/components/error-alert/ErrorAlert';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle
} from '@/components/ui/dialog';
import Spinner from '@/components/spinner/Spinner';
import { useRevalidator } from 'react-router';
import { ProcessObjectFile } from '@/routes/process-objects/list/types';
import { poFileUpdateSchema, UpdateProcessObjectFileFormSchema } from './schemas';

type ProcessObjectFileUpdateModalProps = {
	isOpen: boolean;
	onClose: () => void;
	measurementGroupName: string;
	file: ProcessObjectFile | null;
};

export default function ProcessObjectFileUpdateModal({
	isOpen,
	onClose,
	measurementGroupName,
	file
}: ProcessObjectFileUpdateModalProps) {
	const [error, setError] = useState('');
	const revalidator = useRevalidator();

	const form = useForm<UpdateProcessObjectFileFormSchema>({
		resolver: zodResolver(poFileUpdateSchema),
		defaultValues: { file: undefined }
	});

	const { mutate: uploadFile, isPending } = useMutation({
		mutationFn: ({ processObjectId, fileId, file }: { processObjectId: number; fileId: number; file: File }) =>
			updateProcessObjectFile(processObjectId, fileId, file),
		mutationKey: ['measurement-groups', 'files'],
		onSuccess: () => {
			form.reset();
			setError('');
			onClose();
			toast('The file has been updated successfully.');
			revalidator.revalidate();
		},
		onError: err => {
			setError(getUIErrorMessage(err));
		}
	});

	const handleClose = () => {
		form.reset();
		setError('');
		onClose();
	};

	const handleSubmit = form.handleSubmit(values => {
		if (!file || !values.file) return;
		uploadFile({
			processObjectId: file.processObjectId,
			fileId: file.id,
			file: values.file
		});
	});

	const title = file
		? `Update file for ${measurementGroupName}`
		: 'Update file';

	return (
		<Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
					{file && (
						<DialogDescription>
							Replace the current file &quot;{file.name}&quot; with a new file.
						</DialogDescription>
					)}
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={handleSubmit} className="flex flex-col gap-5 mt-2">
						<FormField
							name="file"
							control={form.control}
							render={({ field }) => (
								<FormItem>
									<FormLabel>Upload file</FormLabel>
									<FormControl>
										<Input
											type="file"
											className="cursor-pointer leading-7"
											onChange={e => field.onChange(e.target.files?.[0])}
											onBlur={field.onBlur}
											name={field.name}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						{error && <ErrorAlert title="Error" description={error} />}
						<DialogFooter>
							<Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
								Cancel
							</Button>
							<Button type="submit" disabled={isPending}>
								Upload
								{isPending && <Spinner className="ml-2" />}
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
}
